import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface MediaFile {
  filename: string;
  url: string;
  size: number;
  uploadDate: string;
}

interface UploadPreview {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
  size: string;
  type: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || '';

async function apiRequest(endpoint: string, options: Record<string, unknown> = {}) {
  const token = localStorage.getItem('saas_token');
  const tenantId = localStorage.getItem('saas_tenant_id');
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> || {}) };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;

  const res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || json.message || 'Request failed');
  return json;
}

export default function MediaPage() {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<MediaFile | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [uploadPreviews, setUploadPreviews] = useState<UploadPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest('/api/media');
      setMediaFiles(res.data || []);
    } catch {
      // API not available — use empty list
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const filtered = useMemo(() => {
    if (!search) return mediaFiles;
    const q = search.toLowerCase();
    return mediaFiles.filter(m => m.filename.toLowerCase().includes(q));
  }, [mediaFiles, search]);

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setActionMsg({ type, text });
    setTimeout(() => setActionMsg(null), 3000);
  }, []);

  const toggleSelect = (filename: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename); else next.add(filename);
      return next;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const previews: UploadPreview[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      previews.push({
        id: `preview-${Date.now()}-${i}`,
        file,
        previewUrl: URL.createObjectURL(file),
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
      });
    }
    setUploadPreviews(prev => [...prev, ...previews]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePreview = (id: string) => {
    const preview = uploadPreviews.find(p => p.id === id);
    if (preview) URL.revokeObjectURL(preview.previewUrl);
    setUploadPreviews(prev => prev.filter(p => p.id !== id));
  };

  const handleUpload = async () => {
    if (uploadPreviews.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      uploadPreviews.forEach(p => formData.append('images', p.file));

      const token = localStorage.getItem('saas_token');
      const tenantId = localStorage.getItem('saas_tenant_id');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = 'Bearer ' + token;
      if (tenantId) headers['X-Tenant-ID'] = tenantId;

      const res = await fetch(`${API_BASE}/api/media/upload`, { method: 'POST', headers, body: formData });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Upload failed');

      setUploadPreviews(prev => { prev.forEach(p => URL.revokeObjectURL(p.previewUrl)); return []; });
      showToast('success', `${uploadPreviews.length} file(s) uploaded!`);
      loadMedia();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename: string) => {
    try {
      await apiRequest(`/api/media/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      setMediaFiles(prev => prev.filter(f => f.filename !== filename));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(filename); return n; });
      setSelectedItem(null);
      showToast('success', `"${filename}" deleted.`);
    } catch {
      showToast('error', 'Failed to delete file.');
    }
  };

  const handleBulkDelete = async () => {
    const files = Array.from(selectedIds);
    if (files.length === 0) return;
    let count = 0;
    for (const f of files) {
      try {
        await apiRequest(`/api/media/${encodeURIComponent(f)}`, { method: 'DELETE' });
        count++;
      } catch { /* skip failures */ }
    }
    setSelectedIds(new Set());
    showToast('success', `${count} file(s) deleted.`);
    loadMedia();
  };

  const copyUrl = (url: string) => {
    const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => showToast('success', 'URL copied!'));
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Media Library</h1>
            <p className="text-xs text-foreground-500">
              {loading ? 'Loading...' : `${filtered.length} file${filtered.length !== 1 ? 's' : ''}`}
              {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-500/10 text-rose-400 text-sm font-semibold hover:bg-rose-500/20 border border-rose-500/20 cursor-pointer whitespace-nowrap">
                <i className="ri-delete-bin-line" /> Delete ({selectedIds.size})
              </button>
            )}
            <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
              <i className="ri-upload-line" /> Upload
            </button>
          </div>
        </div>

        {uploadPreviews.length > 0 && (
          <div className="rounded-xl border border-accent-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-image-add-line text-accent-700" />
                <span className="text-sm font-semibold text-foreground-950">Upload Queue</span>
                <span className="text-[10px] text-foreground-500 bg-background-200/60 px-2 py-0.5 rounded-full">{uploadPreviews.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { uploadPreviews.forEach(p => URL.revokeObjectURL(p.previewUrl)); setUploadPreviews([]); }} className="px-3 py-1.5 rounded-lg text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Clear</button>
                <button onClick={handleUpload} disabled={uploading} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                  <i className={`${uploading ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} text-xs`} />
                  {uploading ? 'Uploading...' : `Upload ${uploadPreviews.length}`}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {uploadPreviews.map(p => (
                <div key={p.id} className="relative group w-28">
                  <div className="aspect-square rounded-lg bg-white overflow-hidden border border-background-200/60">
                    <img src={p.previewUrl} alt={p.name} className="w-full h-full object-cover" />
                    <button onClick={() => removePreview(p.id)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      <i className="ri-close-line text-white text-[10px]" />
                    </button>
                  </div>
                  <p className="text-[10px] text-foreground-500 truncate mt-1">{p.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input type="text" placeholder="Search files..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
          </div>
          <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 ml-auto">
            <button onClick={() => setViewMode('grid')} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${viewMode === 'grid' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500'}`}><i className="ri-layout-grid-line" /></button>
            <button onClick={() => setViewMode('list')} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${viewMode === 'list' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500'}`}><i className="ri-list-view" /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="w-10 h-10 rounded-full border-2 border-accent-500 border-t-transparent animate-spin mx-auto mb-3" />
            <p className="text-sm text-foreground-500">Loading media library...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center rounded-xl border border-background-200/60 bg-white">
            <i className="ri-image-line text-5xl text-foreground-400 mb-4 block" />
            <p className="text-lg font-semibold text-foreground-950 mb-1">No media files</p>
            <p className="text-sm text-foreground-500">{search ? 'Try adjusting your search.' : 'Upload your first image to get started.'}</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map(file => (
              <div key={file.filename} className={`rounded-xl border overflow-hidden transition-all group cursor-pointer ${selectedIds.has(file.filename) ? 'border-accent-500/50 bg-accent-50' : 'border-background-200/60 bg-white hover:border-accent-200'}`}>
                <div className="aspect-square bg-white overflow-hidden flex items-center justify-center relative" onClick={() => setSelectedItem(file)}>
                  <img src={`${API_BASE}${file.url}`} alt={file.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div onClick={(e) => { e.stopPropagation(); toggleSelect(file.filename); }} className={`absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center cursor-pointer ${selectedIds.has(file.filename) ? 'bg-accent-500 text-white opacity-100' : 'bg-white/80 opacity-0 group-hover:opacity-100'}`}>
                    {selectedIds.has(file.filename) ? <i className="ri-check-line text-xs" /> : <i className="ri-checkbox-blank-line text-foreground-400 text-sm" />}
                  </div>
                </div>
                <div className="p-2.5" onClick={() => setSelectedItem(file)}>
                  <p className="text-xs text-foreground-950 truncate font-medium">{file.filename}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-foreground-500">{formatFileSize(file.size)}</span>
                    <span className="text-[10px] text-foreground-500">{formatDate(file.uploadDate)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/60">
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">File</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Size</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Uploaded</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(file => (
                  <tr key={file.filename} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors cursor-pointer">
                    <td className="px-4 py-3" onClick={() => setSelectedItem(file)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex-shrink-0">
                          <img src={`${API_BASE}${file.url}`} alt="" className="w-full h-full object-cover" />
                        </div>
                        <span className="text-sm text-foreground-950 truncate max-w-[300px]">{file.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500" onClick={() => setSelectedItem(file)}>{formatFileSize(file.size)}</td>
                    <td className="px-4 py-3 text-xs text-foreground-500" onClick={() => setSelectedItem(file)}>{formatDate(file.uploadDate)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); copyUrl(`${API_BASE}${file.url}`); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer" title="Copy URL"><i className="ri-file-copy-line text-xs" /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(file.filename); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-rose-500/10 text-foreground-500 hover:text-rose-400 cursor-pointer" title="Delete"><i className="ri-delete-bin-line text-xs" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedItem && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedItem(null)} />
          <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/60">
              <h3 className="font-heading text-lg font-bold text-foreground-950 truncate">{selectedItem.filename}</h3>
              <button onClick={() => setSelectedItem(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-xl overflow-hidden bg-white">
                <img src={`${API_BASE}${selectedItem.url}`} alt={selectedItem.filename} className="w-full object-cover max-h-72" />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Size</span><span className="text-foreground-950">{formatFileSize(selectedItem.size)}</span></div>
                <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Uploaded</span><span className="text-foreground-950">{formatDate(selectedItem.uploadDate)}</span></div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => handleDelete(selectedItem.filename)} className="flex-1 py-2 rounded-lg bg-rose-500/10 text-rose-400 text-sm font-semibold hover:bg-rose-500/20 cursor-pointer whitespace-nowrap">Delete</button>
                <button onClick={() => copyUrl(`${API_BASE}${selectedItem.url}`)} className="flex-1 py-2 rounded-lg border border-background-200/60 text-foreground-500 text-sm hover:text-foreground-950 cursor-pointer whitespace-nowrap">Copy URL</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}